import {action, observable, makeObservable} from 'mobx';
import {getEditedFields} from '../screens/CompanyDetails/utils';
import {formName} from '../screens/CompanyDetails/constants';
import {OB_Screen} from './OB_Screen';
import {onboardingMessageStore} from './OB_Message_Store';
import {getForm, saveRecord, startRecordEdit, updateRecordField} from '../service/api/general';
import {
    findColumnByName,
    mapRecordByColumns,
    mapRecordValue
} from '../../../utils/proto-api-utils/utils';
import {getCompanyData} from '../service/api/company_data';

type CompanyData = any;

export type SaveDataType = {
    [key: string]: any;
};

class OB_Company_Store extends OB_Screen {
    defaultCompanyData = null;
    editRecordMode: boolean = false;
    isReadOnly: boolean = false;

    @observable companyData?: CompanyData | null;

    resetStore = () => {
        // reset store to default values
        this.setCompanyData(null);
        this.editRecordMode = false;
        this.isReadOnly = false;
    };

    @action setCompanyData = companyData => {
        this.defaultCompanyData = companyData;
        this.companyData = companyData;
    };

    @action setIsReadOnly = readOnly => {
        this.isReadOnly = readOnly;
    };

    requestCompanyData = async (form, cols) => {
        if (!this.companyData) {
            const records = await getCompanyData(form, cols);
            const companyData = mapRecordByColumns(records.Records, cols);

            return companyData[0];
        }
        return this.companyData;
    };

    runEditAction = async (Session: string) => {
        if (!this.editRecordMode) {
            await startRecordEdit({
                Session,
                ScrLine: 0,
                Keys: null,
                formName
            });
        }

        this.editRecordMode = true;
    };

    fieldUpdate = async (value, name) => {
        const {
            form: {Session},
            cols
        } = await getForm(formName);

        const Column = findColumnByName(cols.Columns, name);
        const ColumnID = Column?.ID;

        const Value = mapRecordValue(value, Column?.Type);

        try {
            const res = await updateRecordField({Value, Session, ColumnID, Fname: formName});

            if (res.Record) {
                this.defaultCompanyData = mapRecordByColumns([res.Record], cols)[0];
            }
        } catch (e) {
            this.handleError(e);
        }
    };

    getCompanyForm = async () => {
        onboardingMessageStore.clearMessage();

        try {
            this.setLoading(true);

            const {form, cols} = await getForm(formName);
            const companyData = await this.requestCompanyData(form, cols);

            if (form.Privileges !== 2) {
                await this.runEditAction(form.Session); // Start Editing the Company record
            } else {
                this.setIsReadOnly(true);
            }

            this.setCompanyData(companyData);

            this.setLoading(false);
        } catch (e) {
            this.handleError(e);
        }
    };

    saveCompanyData = async (formData: SaveDataType) => {
        try {
            const {form} = await getForm(formName);

            this.setSubmitted(true);

            // Check if which cols were changed
            const editedCols = getEditedFields(formData, this.defaultCompanyData);

            if (Object.keys(editedCols).length > 0) {
                for (const col of editedCols) {
                    await this.fieldUpdate(col.value, col.name);
                }

                // Save Company data
                await saveRecord(form.Session);

                this.setCompanyData({...this.companyData, ...formData});
                this.editRecordMode = false;
            }

            this.setSubmitted(false);
        } catch (e) {
            this.setSubmitted(false);
            this.handleError(e);
        }
    };

    handleError = e => {
        this.setLoading(false);
        return;
    };

    constructor() {
        super();

        makeObservable(this);
    }
}

export const companyFormStore = new OB_Company_Store();
